import { useState, useEffect } from 'react';
import { Container } from '@app/components/Container';
import { PageHeader } from '@app/components/PageHeader';
import { LoadingSpinner } from '@app/components/common/LoadingSpinner';
import { useError } from '@app/hooks/useError';
import Logger from '@app/utils/logger';
import { DisseminationService } from '@app/api/dissemination';
import { DisseminationStatusResponse } from '@app/api/dissemination/types';
import { SharedTimeline } from './components/SharedTimeline';
import { FORECAST_MODELS } from '@app/common/static-data/models';

// Helper function to get model display name
function getModelDisplayName(modelId: string): string {
  const model = FORECAST_MODELS.find((m) => m.id === modelId);
  return model?.label || modelId.toUpperCase();
}

const MODELS = [
  'ept2',
  'ept2_early',
  'ept2_e',
  'ept2_rr',
  'ept2_hrrr',
  'ept2_reasoning',
  'ept1_5',
  'ept1_5_early',
  'aifs',
  'aurora',
];

export default function DisseminationStatus() {
  const [statusData, setStatusData] = useState<DisseminationStatusResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const { showError } = useError();
  const [dayRange, setDayRange] = useState<number>(7);

  useEffect(() => {
    let isMounted = true;

    const loadDisseminationStatus = async () => {
      if (!isMounted) return;
      setLoading(true);

      try {
        const since = new Date(Date.now() - dayRange * 24 * 60 * 60 * 1000);
        const data = await DisseminationService.getStatus(MODELS, since);

        if (isMounted) {
          setStatusData(data);
        }
      } catch (error) {
        Logger.error('Failed to load dissemination status', error);
        showError({
          code: 'DISSEMINATION_STATUS_ERROR',
          message: 'Failed to load forecast dissemination status',
          severity: 'error',
          suggestion: 'Please try again later.',
        });
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadDisseminationStatus();

    return () => {
      isMounted = false;
    };
  }, [showError, dayRange]);

  return (
    <Container>
      <PageHeader
        title="Forecast Dissemination Status"
        description="Monitor forecast delivery timeliness and performance across all models"
      />

      <div className="mb-6">
        <div className="bg-white dark:bg-theme-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-theme-dark-text-secondary mb-2">
            Time Range
          </label>
          <div className="flex gap-2">
            {[1, 3, 7, 14, 30].map((days) => (
              <button
                key={days}
                onClick={() => setDayRange(days)}
                disabled={loading}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  dayRange === days
                    ? 'bg-[#E34290] text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {days} {days === 1 ? 'Day' : 'Days'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center h-[400px] space-y-4">
          <LoadingSpinner size="lg" />
          <p className="text-gray-600 dark:text-theme-dark-text-secondary">
            Loading dissemination status data...
          </p>
        </div>
      ) : statusData && statusData.status_per_model ? (
        <SharedTimeline statusPerModel={statusData.status_per_model} modelOrder={MODELS} />
      ) : (
        <div className="flex flex-col items-center justify-center h-[400px] text-gray-500 dark:text-theme-dark-text-secondary">
          <p>No dissemination status data available.</p>
        </div>
      )}

      {/* Statistics Summary */}
      {statusData && statusData.status_per_model && !loading && (
        <div className="mt-6 bg-white dark:bg-theme-dark-bg rounded-lg border border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-bold mb-4 dark:text-theme-dark-text-primary">
            Summary Statistics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {MODELS.filter((modelId) => statusData.status_per_model[modelId]).map((modelId) => {
              const modelStatus = statusData.status_per_model[modelId];
              const totalForecasts = modelStatus.status.length;
              const missingCount = modelStatus.status.filter((f) => f.is_missing).length;
              const delayedCount = modelStatus.status.filter(
                (f) => !f.is_missing && f.delay !== null && f.delay >= 0,
              ).length;
              const onTimeCount = modelStatus.status.filter(
                (f) => !f.is_missing && f.delay !== null && f.delay < 0,
              ).length;

              const missingPercentage = ((missingCount / totalForecasts) * 100).toFixed(1);
              const onTimePercentage = ((onTimeCount / totalForecasts) * 100).toFixed(1);

              return (
                <div
                  key={modelId}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                >
                  <div className="font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    {getModelDisplayName(modelId)}
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Total:</span>
                      <span className="text-gray-900 dark:text-gray-100">{totalForecasts}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-green-600 dark:text-green-400">On Time:</span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {onTimeCount} ({onTimePercentage}%)
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-yellow-600 dark:text-yellow-400">Delayed:</span>
                      <span className="text-gray-900 dark:text-gray-100">{delayedCount}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">Missing:</span>
                      <span className="text-gray-900 dark:text-gray-100">
                        {missingCount} ({missingPercentage}%)
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Container>
  );
}
